import { UserActivationStatus, UserAppearance } from "@src/gql/graphql";
import { z } from "zod";
import {
	buildFullName,
	computeEffectiveDisplayName,
	computePreferredName,
} from "./name-utils";

const avatarSchema = z.string();
//for cases when user is just invited and we only fetched him for the first time
const nameSchema = z.string().default("");
const displayNameSchema = z.string();

const baseSchema = z.object({
	avatarUrl: avatarSchema,
	firstName: nameSchema,
	lastName: nameSchema,
});

export const userStoreSchema = z.preprocess(
	(data) => {
		if (typeof data !== "object" || data === null) {
			return data;
		}

		const obj = data as Record<string, unknown>;

		const firstNameRaw = obj.firstName;
		const lastNameRaw = obj.lastName;
		const displayNameRaw = obj.displayName;
		const emailRaw = obj.email;

		const fullName = buildFullName(firstNameRaw, lastNameRaw).trim();
		const effectiveDisplayName = computeEffectiveDisplayName(
			firstNameRaw,
			lastNameRaw,
			displayNameRaw,
		);
		const preferredName = computePreferredName(
			firstNameRaw,
			lastNameRaw,
			displayNameRaw,
			emailRaw,
		);

		const zoomSpeedRaw = obj.zoomSpeed;
		let zoomSpeed: unknown = zoomSpeedRaw;
		if (zoomSpeedRaw !== null && zoomSpeedRaw !== undefined) {
			const parsed =
				typeof zoomSpeedRaw === "number"
					? zoomSpeedRaw
					: Number.parseFloat(String(zoomSpeedRaw));
			zoomSpeed = Number.isNaN(parsed) ? undefined : parsed;
		}

		return {
			...obj,
			fullName,
			displayName: effectiveDisplayName,
			preferredName,
			zoomSpeed,
		};
	},
	baseSchema.extend({
		id: z.string(),
		avatarUrl: avatarSchema.nullable(),
		activationStatus: z.nativeEnum(UserActivationStatus),
		fullName: z.string(),
		preferredName: z.string(),
		displayName: displayNameSchema,
		email: z.string().email(),
		language: z.string().nullable(),
		phoneNumber: z.string().nullable(),
		presenceColor: z.string().nullable().optional(),
		timezone: z.string().nullable(),
		referralCode: z.string().nullish(),
		newEmailToConfirm: z.string().nullable().optional(),
		zoomSpeed: z.number().nullable().optional(),
		appearance: z.nativeEnum(UserAppearance).nullable().optional(),
	}),
);

export const userInsertSchema = baseSchema.extend({
	email: z.string().email(),
	externalId: z.string().min(1),
	idProvider: z.literal("auth0"),
});

export const userUpdateRequestSchema = baseSchema.extend({
	displayName: displayNameSchema,
	language: z.string(),
	timezone: z.string(),
});

export const userUpdateSchema = baseSchema
	.extend({
		displayName: displayNameSchema,
		languageCode: z.string(),
		timeZoneId: z.string(),
	})
	.transform((data) => {
		return {
			...data,
			language: data.languageCode,
			timezone: data.timeZoneId,
		};
	});

export type UserStore = z.infer<typeof userStoreSchema>;
export type UserRegister = z.infer<typeof userInsertSchema>;
export type UserUpdateRequest = z.infer<typeof userUpdateRequestSchema>;
